/**
 * ChangePasswordSection — self-service password change from Profile.
 *
 * Distinct from the admin-triggered reset flow (which emails a magic
 * link and doesn't require current-password knowledge). Requires the
 * user to prove they know the current password before the new one
 * is accepted.
 *
 * Behaviour:
 *  • Google/OAuth-only users (no password on file) get a clear error
 *    directing them to "Forgot password" instead — this component
 *    still renders but the submit surfaces the backend 403 verbatim.
 *  • Inline validation for length + confirm-match happens before the
 *    request is fired to save a round-trip.
 *  • On success the form clears; the user's JWT stays valid (no
 *    forced re-login) since the password_hash change alone doesn't
 *    invalidate tokens.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useTranslation } from '../contexts/TranslationContext';

const API = process.env.REACT_APP_BACKEND_URL;

const MIN_PASSWORD_LEN = 6;

export function ChangePasswordSection() {
    const { t } = useTranslation();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Live client-side validation — mirrored server-side, but shown
    // inline so the user gets feedback before hitting Save.
    const inlineError = (() => {
        if (!newPassword && !confirmPassword) return null;
        if (newPassword && newPassword.length < MIN_PASSWORD_LEN) {
            return t('profile.password.err_too_short', 'New password must be at least {n} characters').replace('{n}', MIN_PASSWORD_LEN);
        }
        if (newPassword && confirmPassword && newPassword !== confirmPassword) {
            return t('profile.password.err_mismatch', 'New password and confirmation do not match');
        }
        if (newPassword && currentPassword && newPassword === currentPassword) {
            return t('profile.password.err_same_as_current', 'New password must be different from current password');
        }
        return null;
    })();

    const canSubmit = currentPassword && newPassword && confirmPassword && !inlineError && !submitting;

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API}/api/auth/change-password`,
                {
                    current_password: currentPassword,
                    new_password: newPassword,
                    confirm_new_password: confirmPassword,
                },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            toast.success(t('profile.password.success', 'Password changed successfully'));
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            // Surface the backend's actionable detail verbatim so the
            // user knows WHY it failed (wrong current password / Google
            // account / too short / mismatch).
            toast.error(err?.response?.data?.detail || t('profile.password.err_generic', 'Failed to change password'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="border-t pt-5 mt-5"
            data-testid="change-password-section"
        >
            <h3 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-indigo-500" />
                {t('profile.password.title', 'Change password')}
            </h3>
            <p className="text-xs text-slate-500 mb-4">
                {t('profile.password.help', 'You\'ll need to enter your current password to confirm this change. Signed in via Google only? Use "Forgot password" from the login screen to set a password first.')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-3 max-w-md">
                <div className="space-y-1.5">
                    <Label htmlFor="current-password" className="text-xs">{t('profile.password.current', 'Current password')}</Label>
                    <div className="relative">
                        <Input
                            id="current-password"
                            type={showCurrent ? 'text' : 'password'}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            autoComplete="current-password"
                            data-testid="current-password-input"
                            disabled={submitting}
                            className="pr-10"
                        />
                        <button
                            type="button"
                            onClick={() => setShowCurrent(v => !v)}
                            className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-600"
                            aria-label={showCurrent ? t('profile.password.hide', 'Hide password') : t('profile.password.show', 'Show password')}
                            tabIndex={-1}
                        >
                            {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="new-password" className="text-xs">{t('profile.password.new', 'New password')}</Label>
                    <div className="relative">
                        <Input
                            id="new-password"
                            type={showNew ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            autoComplete="new-password"
                            data-testid="new-password-input"
                            disabled={submitting}
                            minLength={MIN_PASSWORD_LEN}
                            className="pr-10"
                        />
                        <button
                            type="button"
                            onClick={() => setShowNew(v => !v)}
                            className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-600"
                            aria-label={showNew ? t('profile.password.hide', 'Hide password') : t('profile.password.show', 'Show password')}
                            tabIndex={-1}
                        >
                            {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="confirm-password" className="text-xs">{t('profile.password.confirm', 'Confirm new password')}</Label>
                    <Input
                        id="confirm-password"
                        type={showNew ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                        data-testid="confirm-password-input"
                        disabled={submitting}
                        minLength={MIN_PASSWORD_LEN}
                    />
                </div>

                {inlineError && (
                    <p
                        className="text-xs text-red-600"
                        data-testid="change-password-inline-error"
                    >
                        {inlineError}
                    </p>
                )}

                <div className="pt-1">
                    <Button
                        type="submit"
                        size="sm"
                        disabled={!canSubmit}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="submit-change-password-btn"
                    >
                        {submitting ? (
                            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        ) : (
                            <KeyRound className="h-4 w-4 mr-1.5" />
                        )}
                        {submitting ? t('profile.password.updating', 'Updating…') : t('profile.password.title', 'Change password')}
                    </Button>
                </div>
            </form>
        </div>
    );
}

export default ChangePasswordSection;
